import React, { useEffect, useRef } from 'react';
import useReducedMotion from '../../hooks/useReducedMotion';
import useIsDark from '../../hooks/useIsDark';

const PARTICLE_COUNT = 100;
const MAX_SPEED = 0.3;
const CURSOR_RADIUS = 100;
const CURSOR_FORCE = 2;
const ATTRACT_DURATION = 600;
const RIPPLE_DURATION = 800;
const RIPPLE_MAX_RADIUS = 350;

function createRippleSound() {
  let audioCtx = null;
  let buffer = null;
  let loading = false;

  function ensureCtx() {
    if (!audioCtx) {
      audioCtx = new (window.AudioContext || window.webkitAudioContext)();
    }
    return audioCtx;
  }

  function loadBuffer() {
    if (buffer || loading) return;
    loading = true;
    const ctx = ensureCtx();
    fetch(`${process.env.PUBLIC_URL}/sounds/impact.mp3`)
      .then((res) => res.arrayBuffer())
      .then((buf) => ctx.decodeAudioData(buf))
      .then((decoded) => { buffer = decoded; })
      .catch(() => { loading = false; });
  }

  return {
    preload() { loadBuffer(); },
    play(xPosition = 0, screenWidth = 1) {
      if (!buffer) { loadBuffer(); return; }
      const ctx = ensureCtx();
      if (ctx.state === 'suspended') ctx.resume();

      const source = ctx.createBufferSource();
      source.buffer = buffer;
      source.playbackRate.value = 0.9 + Math.random() * 0.2;

      const gain = ctx.createGain();
      gain.gain.value = 0.4;

      const panner = ctx.createStereoPanner();
      panner.pan.value = Math.max(-1, Math.min(1, (xPosition / screenWidth) * 2 - 1));

      source.connect(gain).connect(panner).connect(ctx.destination);
      source.start();
    },
    dispose() {
      if (audioCtx) audioCtx.close();
    },
  };
}

export default function ParticleBackground() {
  const canvasRef = useRef(null);
  const particlesRef = useRef([]);
  const mouseRef = useRef({ x: -1000, y: -1000 });
  const rafRef = useRef(null);
  const { isDarkRef } = useIsDark();
  const attractingRef = useRef(false);
  const attractTimerRef = useRef(null);
  const ripplesRef = useRef([]);
  const rippleSoundRef = useRef(null);
  const reducedMotion = useReducedMotion();

  useEffect(() => {
    rippleSoundRef.current = createRippleSound();
    if (!reducedMotion) rippleSoundRef.current.preload();
    return () => {
      if (rippleSoundRef.current) rippleSoundRef.current.dispose();
    };
  }, [reducedMotion]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    let prevW = window.innerWidth;
    let prevH = window.innerHeight;
    const resize = () => {
      const newW = window.innerWidth;
      const newH = window.innerHeight;
      canvas.width = newW;
      canvas.height = newH;
      // Rescale particle positions proportionally so they stay on screen
      if (prevW && prevH) {
        const sx = newW / prevW;
        const sy = newH / prevH;
        particlesRef.current.forEach((p) => {
          p.x *= sx;
          p.y *= sy;
        });
      }
      prevW = newW;
      prevH = newH;
    };
    resize();
    window.addEventListener('resize', resize);

    // Initialize particles
    particlesRef.current = Array.from({ length: PARTICLE_COUNT }, () => ({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height,
      vx: (Math.random() - 0.5) * MAX_SPEED,
      vy: (Math.random() - 0.5) * MAX_SPEED,
      baseVx: (Math.random() - 0.5) * 0.15,
      baseVy: (Math.random() - 0.5) * 0.15,
      size: Math.random() * 2 + 0.5,
      opacity: Math.random() * 0.3 + 0.05,
    }));

    // If reduced motion, draw static particles once and stop
    if (reducedMotion) {
      const color = isDarkRef.current ? '255, 255, 255' : '0, 0, 0';
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particlesRef.current.forEach((p) => {
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${color}, ${p.opacity})`;
        ctx.fill();
      });
      return () => {
        window.removeEventListener('resize', resize);
      };
    }

    const handleMouseMove = (e) => {
      mouseRef.current = { x: e.clientX, y: e.clientY };
    };
    const handleTouchMove = (e) => {
      const touch = e.touches[0];
      mouseRef.current = { x: touch.clientX, y: touch.clientY };
    };
    const handleMouseLeave = () => {
      mouseRef.current = { x: -1000, y: -1000 };
    };
    const handleTouchEnd = () => {
      mouseRef.current = { x: -1000, y: -1000 };
    };
    const handleClick = (e) => {
      // Ignore clicks on interactive elements (buttons, links, etc.)
      const tag = e.target.closest('button, a, [role="button"]');
      if (tag) return;

      if (isDarkRef.current) {
        // Inward ripple effect in dark mode — no particle attract
        ripplesRef.current.push({
          x: e.clientX,
          y: e.clientY,
          startTime: performance.now(),
        });
        if (rippleSoundRef.current) {
          rippleSoundRef.current.play(e.clientX, window.innerWidth);
        }
        return;
      }

      attractingRef.current = true;
      clearTimeout(attractTimerRef.current);
      attractTimerRef.current = setTimeout(() => {
        attractingRef.current = false;
      }, ATTRACT_DURATION);
    };
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('touchmove', handleTouchMove, { passive: true });
    window.addEventListener('mouseleave', handleMouseLeave);
    window.addEventListener('touchend', handleTouchEnd);
    window.addEventListener('mousedown', handleClick);

    const animate = () => {
      if (document.hidden) {
        rafRef.current = requestAnimationFrame(animate);
        return;
      }
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Only render in dark mode — light mode uses bubbles instead
      if (!isDarkRef.current) {
        rafRef.current = requestAnimationFrame(animate);
        return;
      }

      const color = '255, 255, 255';
      const now = performance.now();

      const hasActiveRipple = ripplesRef.current.some((r) => (now - r.startTime) / RIPPLE_DURATION < 1);

      particlesRef.current.forEach((p) => {
        // Cursor interaction — suppressed while ripples are active
        if (!hasActiveRipple) {
          const dx = p.x - mouseRef.current.x;
          const dy = p.y - mouseRef.current.y;
          const dist = Math.sqrt(dx * dx + dy * dy);

          if (dist < CURSOR_RADIUS && dist > 0) {
            const force = (1 - dist / CURSOR_RADIUS) * CURSOR_FORCE * 0.03;
            p.vx += (dx / dist) * force;
            p.vy += (dy / dist) * force;
          }
        }

        // Ripple attraction — pull particles toward each active ripple center
        for (let r = 0; r < ripplesRef.current.length; r++) {
          const ripple = ripplesRef.current[r];
          const elapsed = now - ripple.startTime;
          const progress = elapsed / RIPPLE_DURATION;
          if (progress >= 1) continue;

          const rdx = p.x - ripple.x;
          const rdy = p.y - ripple.y;
          const rDist = Math.sqrt(rdx * rdx + rdy * rdy);
          const rippleRadius = RIPPLE_MAX_RADIUS * (1 - progress);

          if (rDist < rippleRadius && rDist > 0) {
            const sweetSpot = 60;
            const strength = (1 - progress) * 4;
            const attract = (1 - rDist / rippleRadius) * strength;
            const repel = Math.max(0, 1 - rDist / sweetSpot) * 3.5;
            const force = attract - repel;
            p.vx -= (rdx / rDist) * force;
            p.vy -= (rdy / rDist) * force;
          }
        }

        // Damping
        p.vx *= 0.98;
        p.vy *= 0.98;

        // Clamp speed — allow faster movement during ripples
        const maxSpeed = hasActiveRipple ? MAX_SPEED * 10 : MAX_SPEED * 3;
        const speed = Math.sqrt(p.vx * p.vx + p.vy * p.vy);
        if (speed > maxSpeed) {
          p.vx = (p.vx / speed) * maxSpeed;
          p.vy = (p.vy / speed) * maxSpeed;
        }

        p.x += p.vx + p.baseVx;
        p.y += p.vy + p.baseVy;

        // Wrap around edges
        if (p.x < -10) p.x = canvas.width + 10;
        if (p.x > canvas.width + 10) p.x = -10;
        if (p.y < -10) p.y = canvas.height + 10;
        if (p.y > canvas.height + 10) p.y = -10;

        // Draw particle
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${color}, ${p.opacity})`;
        ctx.fill();
      });

      // Draw connections between nearby particles
      for (let i = 0; i < particlesRef.current.length; i++) {
        for (let j = i + 1; j < particlesRef.current.length; j++) {
          const a = particlesRef.current[i];
          const b = particlesRef.current[j];
          const dx = a.x - b.x;
          const dy = a.y - b.y;
          const dist = Math.sqrt(dx * dx + dy * dy);

          if (dist < 120) {
            const opacity = (1 - dist / 120) * 0.08;
            ctx.beginPath();
            ctx.moveTo(a.x, a.y);
            ctx.lineTo(b.x, b.y);
            ctx.strokeStyle = `rgba(${color}, ${opacity})`;
            ctx.lineWidth = 0.5;
            ctx.stroke();
          }
        }
      }

      // Draw inward ripples
      for (let i = ripplesRef.current.length - 1; i >= 0; i--) {
        const ripple = ripplesRef.current[i];
        const elapsed = now - ripple.startTime;
        const progress = elapsed / RIPPLE_DURATION;

        if (progress >= 1) {
          ripplesRef.current.splice(i, 1);
          continue;
        }

        // Ripple contracts inward: starts at max radius, shrinks to 0
        const radius = RIPPLE_MAX_RADIUS * (1 - progress);
        const opacity = (1 - progress) * 0.08;

        // Outer ring — thinnest
        ctx.beginPath();
        ctx.arc(ripple.x, ripple.y, radius, 0, Math.PI * 2);
        ctx.strokeStyle = `rgba(255, 255, 255, ${opacity})`;
        ctx.lineWidth = 0.5;
        ctx.stroke();

        // Middle ring
        const midRadius = radius * 0.6;
        const midOpacity = (1 - progress) * 0.06;
        ctx.beginPath();
        ctx.arc(ripple.x, ripple.y, midRadius, 0, Math.PI * 2);
        ctx.strokeStyle = `rgba(255, 255, 255, ${midOpacity})`;
        ctx.lineWidth = 1.2;
        ctx.stroke();

        // Inner ring — thickest
        const innerRadius = radius * 0.3;
        const innerOpacity = (1 - progress) * 0.05;
        ctx.beginPath();
        ctx.arc(ripple.x, ripple.y, innerRadius, 0, Math.PI * 2);
        ctx.strokeStyle = `rgba(255, 255, 255, ${innerOpacity})`;
        ctx.lineWidth = 2.5 + (1 - progress);
        ctx.stroke();
      }

      rafRef.current = requestAnimationFrame(animate);
    };

    rafRef.current = requestAnimationFrame(animate);

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      window.removeEventListener('resize', resize);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('mouseleave', handleMouseLeave);
      window.removeEventListener('touchend', handleTouchEnd);
      window.removeEventListener('mousedown', handleClick);
      clearTimeout(attractTimerRef.current);
    };
  }, [reducedMotion, isDarkRef]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 0,
      }}
    />
  );
}
